import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import React, { memo, useEffect, useMemo, useState } from 'react'

import RenderException from 'components/work-order/details/exceptions/RenderException'
import { EXCEPTION_TYPES } from 'settings/constants/exceptions'
import { blobToBase64, getExceptionImageBlobFromPath } from 'utils/file'

import type { SxProps, Theme } from '@mui/material/styles'

type Props = {
  path?: string | null
  height?: string | number
  minHeight?: string | number
  ImageSx?: SxProps<Theme>
  size?: 'default' | 'small' | 'medium'
  withSwiperSlide?: boolean
  exceptionType?: string | null
  PageProps?: Record<string, unknown>
}

const ExceptionImageFromPath: React.FC<Props> = ({
  path,
  height = '194px',
  minHeight,
  ImageSx,
  size = 'default',
  withSwiperSlide = false,
  exceptionType,
  PageProps,
}) => {
  const [base64Img, setBase64Img] = useState('')
  const isSignExceptionType = useMemo(() => exceptionType === EXCEPTION_TYPES.Sign, [exceptionType])
  const [isPhotoLoading, setIsPhotoLoading] = useState(false)
  const showImage = base64Img && !isPhotoLoading
  const showSkeleton = !base64Img || isPhotoLoading
  const getCircularProgressSize = (imageSize: Props['size']) => {
    if (imageSize === 'medium') return 40
    if (imageSize === 'small') return 40
    return 64
  }

  const circularProgressSize = getCircularProgressSize(size)

  const getExceptionPhoto = async () => {
    setIsPhotoLoading(true)
    getExceptionImageBlobFromPath(path, isSignExceptionType ? 'default' : size)
      .then(blobToBase64)
      .then(base64 => {
        if (base64) {
          setBase64Img(base64)
          setIsPhotoLoading(false)
        }
      })
      .catch(() => {
        setIsPhotoLoading(false)
      })
      .finally(() => setIsPhotoLoading(false))
  }

  useEffect(() => {
    if (path) getExceptionPhoto()
  }, [path, exceptionType, isSignExceptionType])

  const lazy = useMemo(() => (isSignExceptionType ? 'false' : 'true'), [isSignExceptionType])
  if (withSwiperSlide) {
    return (
      <swiper-slide lazy={lazy}>
        {showImage && (
          <RenderException
            PageProps={PageProps}
            isSignExceptionType={isSignExceptionType}
            base64Data={base64Img}
            ImageSx={ImageSx}
            height={height}
          />
        )}
        {showSkeleton && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={[{ height, minHeight }, ...(Array.isArray(ImageSx) ? ImageSx : [ImageSx])]}
          >
            <CircularProgress size={circularProgressSize} />
          </Stack>
        )}
      </swiper-slide>
    )
  }
  return (
    <>
      {showImage && (
        <RenderException
          base64Data={base64Img}
          height={height}
          ImageSx={ImageSx}
          isSignExceptionType={isSignExceptionType}
          PageProps={PageProps}
        />
      )}
      {showSkeleton && (
        <Stack alignItems="center" justifyContent="center" sx={[{ height, minHeight }, ...(Array.isArray(ImageSx) ? ImageSx : [ImageSx])]}>
          {isPhotoLoading && <CircularProgress size={circularProgressSize} />}
        </Stack>
      )}
    </>
  )
}

export default memo(ExceptionImageFromPath)
